import React, { lazy } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Auth/Login';
import Layout from './components/Layout/Layout';

// Pages are code-split: each one is downloaded the first time it's opened
// instead of shipping every screen (and heavy libs like recharts) up front.
// Layout wraps them in <Suspense>, so the top bar stays put while a page loads.
//
// After a redeploy, a tab that was already open asks for the old chunk file
// names, which no longer exist. Reload once to pick up the new build rather
// than leaving a blank page.
const RELOAD_FLAG = 'chunk-reload-attempted';
const lazyPage = (factory) => lazy(() =>
    factory()
        .then((module) => {
            try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* storage unavailable */ }
            return module;
        })
        .catch((error) => {
            let alreadyTried = true;
            try {
                alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === '1';
                if (!alreadyTried) sessionStorage.setItem(RELOAD_FLAG, '1');
            } catch { /* storage unavailable */ }
            if (!alreadyTried) {
                window.location.reload();
                return new Promise(() => {});
            }
            throw error;
        })
);

const EntryScreen = lazyPage(() => import('./components/Entry/EntryScreen'));
const EntryHistory = lazyPage(() => import('./components/Entry/EntryHistory'));
const Dashboard = lazyPage(() => import('./components/Admin/Dashboard'));
const WriterToday = lazyPage(() => import('./components/Today/WriterToday'));
const BomManager = lazyPage(() => import('./components/Admin/BomManager'));
const StockAudit = lazyPage(() => import('./components/Admin/StockAudit'));
const Reports = lazyPage(() => import('./components/Admin/Reports'));
const JumboAndRewind = lazyPage(() => import('./components/Inventory/JumboAndRewind'));
const StockOnHand = lazyPage(() => import('./components/Inventory/StockOnHand'));
const ReceiveStock = lazyPage(() => import('./components/Inventory/ReceiveStock'));
const StockLedger = lazyPage(() => import('./components/Inventory/StockLedger'));
const MaterialEntry = lazyPage(() => import('./components/Inventory/MaterialEntry'));
const ItemsAndGroups = lazyPage(() => import('./components/Inventory/ItemsAndGroups'));
const EmployeeList = lazyPage(() => import('./components/HR/EmployeeList'));
const DailyAttendance = lazyPage(() => import('./components/HR/DailyAttendance'));
const SalaryProcessing = lazyPage(() => import('./components/HR/SalaryProcessing'));
const Setup = lazyPage(() => import('./components/Setup/Setup'));
const ShiftWiseView = lazyPage(() => import('./components/Shifts/ShiftWiseView'));
const Guide = lazyPage(() => import('./components/Guide/Guide'));

// Loading screen
const LoadingScreen = () => (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-3 bg-background" role="status" aria-live="polite">
        <span className="spinner text-accent" style={{ width: '1.5rem', height: '1.5rem' }} />
        <p className="m-0 text-sm font-semibold text-text-secondary">Loading your workspace…</p>
    </div>
);

// Route guard: `allow` decides from the auth context whether this user may open the page.
const Guard = ({ allow, children }) => {
    const auth = useAuth();

    if (auth.loading) {
        return <LoadingScreen />;
    }

    if (!auth.isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (allow && !allow(auth)) {
        return <Navigate to="/" replace />;
    }

    return <Layout>{children}</Layout>;
};

const anyone = null;
const adminOnly = (a) => a.isAdmin();
const stockRoles = (a) => a.canManageStock();

// "/" is Production entry. The Writer has no shift, so their home is Today.
const Home = () => {
    const { isWriter } = useAuth();
    if (isWriter()) return <Navigate to="/today" replace />;
    return <EntryScreen />;
};

// Admin keeps BOM, Category tree and Item master inside Setup (its menu stays on screen);
// the Writer, who has no Setup, opens Bill of materials as its own page.
const BomPage = () => {
    const { isAdmin } = useAuth();
    const [params] = useSearchParams();
    if (isAdmin()) return <Navigate to={`/setup?s=bom${params.get('film') ? `&film=${params.get('film')}` : ''}`} replace />;
    return <BomManager />;
};

const PAGES = [
    ['/', anyone, <Home />],
    ['/history', anyone, <EntryHistory />],
    ['/shifts', anyone, <ShiftWiseView />],
    ['/today', stockRoles, <WriterToday />],
    ['/admin/dashboard', adminOnly, <Dashboard />],
    ['/setup', adminOnly, <Setup />],

    // HR: Team · Attendance · Payroll · Settings
    ['/hr/team', anyone, <EmployeeList />],
    ['/hr/attendance', anyone, <DailyAttendance />],
    ['/hr/payroll', adminOnly, <SalaryProcessing />],

    // Stock
    ['/inventory/receive', anyone, <ReceiveStock />],
    ['/inventory/material', anyone, <MaterialEntry />],
    ['/inventory/ledger', anyone, <StockLedger />],
    ['/inventory/stock', anyone, <StockOnHand />],
    ['/inventory/jumbo', anyone, <JumboAndRewind />],
    ['/inventory/items', stockRoles, <ItemsAndGroups />],
    ['/admin/bom', stockRoles, <BomPage />],
    ['/admin/stock-audit', stockRoles, <StockAudit />],
    ['/inventory/reports', stockRoles, <Reports />],

    ['/guide', anyone, <Guide />],
];

function App() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />

            {PAGES.map(([path, allow, page]) => (
                <Route key={path} path={path} element={<Guard allow={allow}>{page}</Guard>} />
            ))}

            {/* Old URLs redirect into the new pages */}
            <Route path="/admin/users" element={<Navigate to="/setup?s=users" replace />} />
            <Route path="/admin/categories" element={<Navigate to="/setup?s=categories" replace />} />
            <Route path="/admin/items" element={<Navigate to="/setup?s=items" replace />} />
            <Route path="/admin/film-types" element={<Navigate to="/setup?s=films" replace />} />
            <Route path="/admin/sticker-designs" element={<Navigate to="/setup?s=stickers" replace />} />
            <Route path="/hr/settings" element={<Navigate to="/setup?s=company" replace />} />
            <Route path="/inventory/rewind" element={<Navigate to="/inventory/jumbo" replace />} />
            <Route path="/hr/employees" element={<Navigate to="/hr/team" replace />} />
            <Route path="/hr/attendance/daily" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/calendar" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/ot" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/attendance/report" element={<Navigate to="/hr/attendance" replace />} />
            <Route path="/hr/advances" element={<Navigate to="/hr/payroll?tab=advances" replace />} />
            <Route path="/hr/holidays" element={<Navigate to="/hr/attendance?tab=holidays" replace />} />
            <Route path="/hr/salary" element={<Navigate to="/hr/payroll" replace />} />
            <Route path="/hr/slips" element={<Navigate to="/hr/payroll?tab=past" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default App;
